/**
 * Mapeamento de eventos do histórico para o ator responsável.
 *
 * Por que esse mapper existe:
 * O backend grava `autor = "sistema"` em praticamente todos os eventos
 * (independente de quem realmente fez a ação). O `usuario_tipo` que
 * indicaria a origem REAL (empresa / estudante / cieemg) só está
 * disponível em outros endpoints (ex.: payload embutido no TCE).
 *
 * Para corrigir o card "Identificação incorreta do ator" sem depender
 * de mudança no backend, inferimos o ator a partir do NOME do evento
 * (`evento`), que já carrega a semântica (sufixo _EMPRESA, _ESTUDANTE,
 * etc).
 *
 * Categorias canônicas (alinhadas com style.css .badge-autor-*):
 *   - EMPRESA   → ação feita pela empresa
 *   - ESTUDANTE → ação feita pelo estudante
 *   - CIEE      → ação feita pelo CIEEMG (validação, correção, etc)
 *   - SISTEMA   → ação automática do sistema
 *
 * Quando o backend passar a mandar `usuario_tipo` no payload do
 * /historico-eventos, basta trocar a função `resolverAtorEvento`
 * para priorizar esse campo — a tabela mantém o mesmo contrato.
 */

export const ATORES_CANONICOS = ["EMPRESA", "ESTUDANTE", "CIEE", "SISTEMA"];

export const ATOR_LABEL = {
  EMPRESA: "Empresa",
  ESTUDANTE: "Estudante",
  CIEE: "CIEEMG",
  SISTEMA: "Sistema",
};

export const ATOR_DESCRICAO = {
  EMPRESA: "Ação realizada pela empresa",
  ESTUDANTE: "Ação realizada pelo estudante",
  CIEE: "Ação realizada pelo CIEEMG",
  SISTEMA: "Ação automática do sistema",
};

// Regras de inferência. Cada chave é um padrão (substring) do campo
// `evento` retornado pelo backend. A PRIMEIRA regra que casar vence.
// Mantenha em ordem do mais específico → mais genérico.
const REGRAS_ATOR = [
  // ---- Empresa ----
  { match: /^INICIO_PREENCHIMENTO_EMPRESA$/i, ator: "EMPRESA" },
  { match: /EDICAO_CADASTRO_EMPRESA$/i, ator: "EMPRESA" },
  { match: /EMPRESA$/i, ator: "EMPRESA" },

  // ---- Estudante ----
  { match: /^INICIO_PREENCHIMENTO_ESTUDANTE$/i, ator: "ESTUDANTE" },
  { match: /ESTUDANTE$/i, ator: "ESTUDANTE" },
  { match: /DOCUMENTO_ANEXADO_ESTUDANTE/i, ator: "ESTUDANTE" },
  { match: /ESTAGIARIO/i, ator: "ESTUDANTE" },

  // ---- CIEEMG (validação, correção, reprovação, disponibilização) ----
  { match: /CADASTRO_VALIDADO/i, ator: "CIEE" },
  { match: /CADASTRO_REJEITADO/i, ator: "CIEE" },
  { match: /CORRECAO_SOLICITADA/i, ator: "CIEE" },
  { match: /CONTRATO_CONFECCIONADO/i, ator: "CIEE" },
  { match: /CONTRATO_DISPONIBILIZADO/i, ator: "CIEE" },
  { match: /DOCUMENTO_LIBERADO/i, ator: "CIEE" },
  { match: /DOCUMENTO_REJEITADO/i, ator: "CIEE" },
  { match: /APROVADO/i, ator: "CIEE" },
  { match: /REPROVADO/i, ator: "CIEE" },
  { match: /VALIDACAO/i, ator: "CIEE" },
  { match: /CIEE/i, ator: "CIEE" },

  // ---- Sistema (criação automática, expiração, etc) ----
  { match: /^SISTEMA$/i, ator: "SISTEMA" },
  { match: /EXPIRADO/i, ator: "SISTEMA" },
  { match: /CRIADO_AUTOMATICAMENTE/i, ator: "SISTEMA" },
];

/**
 * Resolve o ator canônico de um evento.
 *
 * Ordem de prioridade:
 *   1) Campo `usuario_tipo` no payload (quando o backend passar a mandar)
 *   2) Campo `autor` se já vier em uma das 4 categorias canônicas
 *      (cobre versões futuras em que o backend conserte a gravação)
 *   3) Inferência pelo `evento` via REGRAS_ATOR
 *   4) Fallback: "SISTEMA"
 *
 * @param {object} item - Item bruto retornado pelo backend
 * @returns {string} Uma das constantes em ATORES_CANONICOS
 */
export function resolverAtorEvento(item) {
  if (!item || typeof item !== "object") return "SISTEMA";

  // 1) usuario_tipo direto do backend (preferência máxima quando vier)
  const usuarioTipo = String(item.usuario_tipo || "").trim().toUpperCase();
  if (usuarioTipo && ATORES_CANONICOS.includes(usuarioTipo)) {
    return usuarioTipo;
  }

  // 2) autor já canônico
  const autorBruto = String(item.autor || "").trim().toUpperCase();
  if (autorBruto && ATORES_CANONICOS.includes(autorBruto)) {
    return autorBruto;
  }

  // 3) Inferência pelo evento
  const evento = String(item.evento || "").trim();
  if (evento) {
    for (const regra of REGRAS_ATOR) {
      if (regra.match.test(evento)) return regra.ator;
    }
  }

  // 4) Fallback
  return "SISTEMA";
}

/**
 * Versão "enriquecida": devolve o ator canônico já acoplado ao item,
 * sem mutar o objeto original. Útil para passar adiante em hooks/pipes.
 */
export function aplicarAtorEvento(item) {
  const ator = resolverAtorEvento(item);
  return { ...item, ator_calculado: ator };
}

/**
 * Aplica o mapper em uma lista inteira. Preserva a ordem original.
 */
export function aplicarAtoresEmLista(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.map(aplicarAtorEvento);
}
